import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '../../../services/api';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import PersonCell from '../../../components/ui/PersonCell';

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $status }) =>
    $status === 'approved' ? '#2e7d3215' :
    $status === 'waiting' || $status === 'submitted' ? '#ed6c0215' :
    $status === 'rejected' ? '#d32f2f15' :
    $status === 'canceled' ? '#9e9e9e15' :
    '#0291EF15'};
  color: ${({ $status }) =>
    $status === 'approved' ? '#2e7d32' :
    $status === 'waiting' || $status === 'submitted' ? '#ed6c02' :
    $status === 'rejected' ? '#d32f2f' :
    $status === 'canceled' ? '#9e9e9e' :
    '#0291EF'};
`;

const SlaBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  margin-left: 6px;
  background: #d32f2f15;
  color: #d32f2f;
`;

const useBenefitApplicationApi = (queryParams, mode = 'all') => {
  const { t, i18n } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  const baseUrl = mode === 'pending'
    ? '/v1/benefit-applications/pending'
    : '/v1/benefit-applications';

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`${baseUrl}?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger, baseUrl]);

  const approve = useCallback(async (id) => {
    try {
      await api.put(`/v1/benefit-applications/${id}/approve`);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  const reject = useCallback(async (id, reason) => {
    try {
      await api.put(`/v1/benefit-applications/${id}/reject`, { rejectionReason: reason });
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const statusLabels = useMemo(() => ({
    draft: t('benefitApplication.statusDraft'),
    submitted: t('benefitApplication.statusSubmitted'),
    waiting: t('benefitApplication.statusWaiting'),
    approved: t('benefitApplication.statusApproved'),
    rejected: t('benefitApplication.statusRejected'),
    canceled: t('benefitApplication.statusCanceled'),
    activated: t('benefitApplication.statusActivated'),
    terminated: t('benefitApplication.statusTerminated'),
  }), [t]);

  const typeLabels = useMemo(() => ({
    'new-benefit': t('benefitApplication.typeNewBenefit'),
    'add-dependent': t('benefitApplication.typeAddDependent'),
    'remove-dependent': t('benefitApplication.typeRemoveDependent'),
    'change-coverage': t('benefitApplication.typeChangeCoverage'),
  }), [t]);

  const statusOptions = useMemo(() => Object.entries(statusLabels).map(([id, name]) => ({ id, name })), [statusLabels]);
  const typeOptions = useMemo(() => Object.entries(typeLabels).map(([id, name]) => ({ id, name })), [typeLabels]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'employeeFullName', label: t('benefitApplication.employee'), options: {
      customBodyRender: (v, tableMeta) => (
        <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.employeeProfilePicture} />
      ), filter: false, sort: true } },
    { name: 'benefitName', label: t('benefitApplication.benefit'), options: { filter: false, sort: false } },
    {
      name: 'applicationType', label: t('benefitApplication.type'),
      options: {
        filter: true, sort: true, filterType: 'dropdown',
        filterOptions: { defaultOp: 'eq', names: typeOptions.map((o) => o.name), renderValue: (v) => v },
        customBodyRender: (v) => typeLabels[v] || v,
      },
    },
    {
      name: 'status', label: t('benefitApplication.status'),
      options: {
        filter: true, sort: true, filterType: 'dropdown',
        filterOptions: { defaultOp: 'eq', names: statusOptions.map((o) => o.name), renderValue: (v) => v },
        customBodyRender: (v, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return (
            <>
              <StatusBadge $status={v}>{statusLabels[v] || v}</StatusBadge>
              {row?.slaBreached ? <SlaBadge>{t('benefitApplication.slaBreached')}</SlaBadge> : null}
            </>
          );
        },
      },
    },
    dateFilterColumn('requestedActivationDate', t('benefitApplication.requestedActivationDate'), i18n.language),
    dateFilterColumn('createDate', t('benefitApplication.submittedDate'), i18n.language),
  ], [t, i18n.language, data, statusLabels, typeLabels, statusOptions, typeOptions]);

  return {
    columns, data, fetching, pagination,
    approve, reject, refresh,
    statusLabels, typeLabels,
  };
};

export default useBenefitApplicationApi;
